import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Menu} from '../../../interface/menu';
import {CreateMenuRequest} from '../../../interface/createMenuRequest';
import {UpdateMenuRequest} from '../../../interface/updateMenuRequest';
import {MenuService} from '../../../service/menu.service';
import {HttpErrorResponse} from '@angular/common/http';
import {noOnlySpacesValidator} from '../../../validator/onlySpacesValidator';
import {ActivatedRoute, Router} from '@angular/router';
import {SnackBarUtil} from '../../../qr-copy-message/snack-bar-util.service';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {MenuShowQrModalComponent} from '../menu-show-qr-modal/menu-show-qr-modal.component';
import {environment} from '../../../../environments/environment';
import {forkJoin} from 'rxjs';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {ImageService} from '../../../service/image.service';
import {TranslateService} from '@ngx-translate/core';
import {GetPageUtil} from '../../get-page-util';

@Component({
  selector: 'app-menu-create-field',
  templateUrl: './menu-create-field.component.html',
  styleUrls: ['./menu-create-field.component.scss']
})
export class MenuCreateFieldComponent implements OnInit {
  @Input() menuToEdit?: Menu;

  menuForm!: FormGroup;
  errorMessages: { [key: string]: string | null } = {
    menu_name: null,
    business_name: null,
    address: null,
    linked_users: null
  };

  availableUsernames: string[] = [];
  mappedUsernames: Array<{ name: string; id: string }> = [];
  isLoadingUsernames: boolean = false;
  isSubmitting: boolean = false;
  isAddPage: boolean = false;
  searchTimeout: ReturnType<typeof setTimeout> | undefined;
  currentSearchPage: number = 0;
  searchPageSize: number = 10;
  noMoreResults: boolean = false;
  currentSearchTerm: string = '';

  menuItems: Array<{
    id: string;
    name: string;
    description: string;
    price?: number;
    image: string | null;
    imageName?: string;
    imageError?: string | null;
    selected?: boolean;
    hasQuantity?: boolean;
    quantity?: string;
    displayOrder?: number;
  }> = [];

  selectedMenuItemId: string | null = null;
  hasSearchField: boolean = false;
  searchQuery: string = '';
  filteredMenuItems: typeof this.menuItems = [];
  currentItemsPage: number = 0;
  itemsPerPage: number = 10;
  private readonly getPageUtil = new GetPageUtil();

  get allowsMultipleProducts(): boolean {
    return this.menuForm.get('allowsMultipleProducts')?.value || false;
  }

  get isMultiSelectAllowed(): boolean {
    return this.menuItems.some(item => !item.hasQuantity) &&
      this.menuItems.some(item => item.hasQuantity);
  }

  private updateMultiSelectAvailability(): void {
    const control = this.menuForm.get('allowsMultipleProducts');
    if (!control) {
      return;
    }
    if (this.isMultiSelectAllowed) {
      control.enable({ emitEvent: false });
    } else {
      control.setValue(false, { emitEvent: false });
      control.disable({ emitEvent: false });
    }
  }
  hasUnsavedChanges: boolean = false;
  mobileStep: 'basic' | 'items' = 'basic';

  constructor(
    private readonly fb: FormBuilder,
    private readonly menuService: MenuService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly snackBar: SnackBarUtil,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly imageService: ImageService,
    private readonly translateService: TranslateService
  ) {
  }

  private isLoadingMenu = false;

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }

  ngOnInit() {
    this.initializeForm();
    this.isAddPage = this.router.url.includes('/menu/add');

    const menuId = this.route.snapshot.paramMap.get('id');
    if (menuId) {
      this.loadMenuAndUsernames(Number(menuId));
    } else if (this.menuToEdit) {
      this.loadAvailableUsernames();
      this.populateFormForEdit();
    } else {
      this.loadAvailableUsernames();
    }
  }

  private loadMenuAndUsernames(menuId: number) {
    if (this.isLoadingMenu || this.isLoadingUsernames) {
      return;
    }

    this.isLoadingMenu = true;
    this.isLoadingUsernames = true;

    forkJoin({
      menu: this.menuService.getMenuById(menuId),
      usernames: this.menuService.getAllCardUsernames(menuId)
    }).subscribe({
      next: ({ menu, usernames }) => {
        this.menuToEdit = menu;
        this.availableUsernames = usernames;
        this.mappedUsernames = usernames.slice(0, this.searchPageSize).map(u => ({ name: u, id: u }));

        this.populateFormForEdit();
        this.addSelectedUsernamesToMapped();

        this.isLoadingMenu = false;
        this.isLoadingUsernames = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoadingMenu = false;
        this.isLoadingUsernames = false;
        if (error.status !== 404) {
          this.snackBar.openSnackBar({
            data: { message: this.translate('menu_management.errors.load_menu') },
            duration: 3000,
            panelClass: SnackBarUtil.CLASSES.error,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
        }
      }
    });
  }

  initializeForm() {
    this.menuForm = this.fb.group({
      menu_name: ['', [
        Validators.required,
        Validators.maxLength(20),
        this.hasLetterOrNumberValidator()
      ]],
      business_name: ['', [
        Validators.maxLength(30),
        this.hasLetterOrNumberValidator()
      ]],
      address: ['', [
        Validators.maxLength(50),
        this.hasLetterOrNumberValidator()
      ]],
      linked_users: [[], [Validators.required]],
      search: [''],
      allowsMultipleProducts: [false]
    });

    this.menuForm.get('menu_name')?.valueChanges.subscribe(() => {
      this.errorMessages['menu_name'] = null;
    });
    this.menuForm.get('business_name')?.valueChanges.subscribe(() => {
      this.errorMessages['business_name'] = null;
    });
    this.menuForm.get('address')?.valueChanges.subscribe(() => {
      this.errorMessages['address'] = null;
    });

    this.updateMultiSelectAvailability();
  }

  populateFormForEdit() {
    if (this.menuToEdit) {
      if (this.availableUsernames.length > 0) {
        this.addSelectedUsernamesToMapped();
      }

      const linkedUsers = this.menuToEdit.linked_users || [];

      this.menuForm.patchValue({
        menu_name: this.menuToEdit.menu_name,
        business_name: this.menuToEdit.business_name || '',
        address: this.menuToEdit.address || '',
        allowsMultipleProducts: this.menuToEdit.allowsMultipleProducts || false
      });

      setTimeout(() => {
        if (!this.menuToEdit) {
          return;
        }

        this.addSelectedUsernamesToMapped();

        const selectedUsernames = this.menuToEdit.linked_users || [];
        selectedUsernames.forEach(username => {
          const exists = this.mappedUsernames.some(u => u.name === username);
          if (!exists && this.availableUsernames.includes(username)) {
            this.mappedUsernames.push({ name: username, id: username });
          }
        });

        const linkedUsersControl = this.menuForm.get('linked_users');
        if (linkedUsersControl) {
          linkedUsersControl.setValue(linkedUsers, { emitEvent: false });
        }

        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
          this.changeDetectorRef.detectChanges();
        }, 50);
      }, 300);

      this.hasSearchField = this.menuToEdit.hasSearchField || false;
      if (this.menuToEdit.menu_items) {
        this.menuItems = this.menuToEdit.menu_items.map((item, index) => ({
          id: item.id || `item-${Date.now()}-${index}`,
          name: item.name || '',
          description: item.description || '',
          price: item.price,
          image: item.image ? this.imageService.resolveImageSrc(item.image) : null,
          hasQuantity: item.hasQuantity || false,
          quantity: item.quantity,
          displayOrder: index
        }));
        this.applySearchFilter();
        this.updateMultiSelectAvailability();
      }
    }
  }

  private addSelectedUsernamesToMapped() {
    if (this.menuToEdit && this.menuToEdit.linked_users && this.availableUsernames.length > 0) {
      const selectedUsernames = this.menuToEdit.linked_users;
      const existingMappedNames = new Set(this.mappedUsernames.map(u => u.name));

      selectedUsernames.forEach(username => {
        if (!existingMappedNames.has(username) && this.availableUsernames.includes(username)) {
          this.mappedUsernames.push({ name: username, id: username });
        }
      });

      this.changeDetectorRef.detectChanges();
    }
  }

  loadAvailableUsernames() {
    if (this.isLoadingUsernames) {
      return;
    }

    this.isLoadingUsernames = true;
    this.menuService.getAllCardUsernames(this.menuToEdit?.id).subscribe({
      next: (usernames: string[]) => {
        this.availableUsernames = usernames;
        this.mappedUsernames = usernames.slice(0, this.searchPageSize).map(u => ({ name: u, id: u }));

        if (this.menuToEdit) {
          this.addSelectedUsernamesToMapped();
        }

        this.isLoadingUsernames = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoadingUsernames = false;
      }
    });
  }

  onSearchUsernames(searchTerm: string) {
    const trimmedQuery = searchTerm.trim();
    this.currentSearchTerm = trimmedQuery;
    if (trimmedQuery.length >= 2) {
      this.isLoadingUsernames = true;
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentSearchPage = 0;
        this.searchUsernames(trimmedQuery, false);
      }, 1000);
    } else if (trimmedQuery.length === 0) {
      clearTimeout(this.searchTimeout);
      this.loadAvailableUsernames();
    } else {
      this.mappedUsernames = [];
      this.isLoadingUsernames = false;
      clearTimeout(this.searchTimeout);
    }
  }

  searchUsernames(query: string, isLoadMore: boolean) {
    this.menuService.searchCardUsernames(query, this.menuToEdit?.id).subscribe({
      next: (usernames: string[]) => {
        this.noMoreResults = true;
        if (isLoadMore) {
          const newMappedUsernames = usernames.map((u: string) => ({ name: u, id: u }));
          this.mappedUsernames = [...this.mappedUsernames, ...newMappedUsernames];
        } else {
          this.mappedUsernames = usernames.map((u: string) => ({ name: u, id: u }));
        }
        this.isLoadingUsernames = false;
      },
      error: () => {
        this.isLoadingUsernames = false;
      }
    });
  }

  loadMoreUsernames() {
    if (!this.noMoreResults && !this.isLoadingUsernames) {
      this.currentSearchPage++;
      this.searchUsernames(this.currentSearchTerm, true);
    }
  }

  hasLetterOrNumberValidator() {
    return (control: FormControl) => {
      if (!control.value?.trim()) {
        return null;
      }
      const hasLetterOrNumber = /[a-zA-Z0-9]/.test(control.value);
      return hasLetterOrNumber ? null : { hasLetterOrNumber: true };
    };
  }

  getErrorMessage(fieldName: string): string | null {
    const control = this.menuForm.get(fieldName);
    if (!control || !control.touched) {
      return null;
    }

    if (control.hasError('required')) {
      if (fieldName === 'menu_name') {
        return 'Enter menu name.';
      }
      if (fieldName === 'linked_users') {
        return this.translate('menu_management.select_linked_user_required');
      }
    }

    if (control.hasError('maxlength')) {
      const maxLength = control.getError('maxlength').requiredLength;
      return `Character limit exceeded (Max ${maxLength}).`;
    }

    if (control.hasError('hasLetterOrNumber')) {
      return 'Must contain at least one letter/number.';
    }

    return null;
  }

  get menuNameControl(): FormControl {
    return this.menuForm.get('menu_name') as FormControl;
  }

  get businessNameControl(): FormControl {
    return this.menuForm.get('business_name') as FormControl;
  }

  get addressControl(): FormControl {
    return this.menuForm.get('address') as FormControl;
  }

  validateForm(): boolean {
    let isValid = true;

    const menuNameControl = this.menuForm.get('menu_name');
    if (menuNameControl?.hasError('required')) {
      this.errorMessages['menu_name'] = this.translate('menu_management.validation.enter_menu_name');
      isValid = false;
    } else if (menuNameControl?.hasError('maxlength')) {
      this.errorMessages['menu_name'] = this.translate('menu_management.validation.max_20');
      isValid = false;
    } else if (menuNameControl?.hasError('hasLetterOrNumber')) {
      this.errorMessages['menu_name'] = this.translate('menu_management.validation.letter_or_number');
      isValid = false;
    }

    const businessNameControl = this.menuForm.get('business_name');
    if (businessNameControl?.hasError('maxlength')) {
      this.errorMessages['business_name'] = this.translate('menu_management.validation.max_30');
      isValid = false;
    } else if (businessNameControl?.hasError('hasLetterOrNumber')) {
      this.errorMessages['business_name'] = this.translate('menu_management.validation.letter_or_number');
      isValid = false;
    }

    const addressControl = this.menuForm.get('address');
    if (addressControl?.hasError('maxlength')) {
      this.errorMessages['address'] = this.translate('menu_management.validation.max_50');
      isValid = false;
    } else if (addressControl?.hasError('hasLetterOrNumber')) {
      this.errorMessages['address'] = this.translate('menu_management.validation.letter_or_number');
      isValid = false;
    }

    const linkedUsersControl = this.menuForm.get('linked_users');
    if (linkedUsersControl?.hasError('required') || !linkedUsersControl?.value || linkedUsersControl.value.length === 0) {
      this.errorMessages['linked_users'] = this.translate('menu_management.select_linked_user_required');
      isValid = false;
    }

    return isValid;
  }

  onSubmit(): void {
    if (this.isSubmitting || this.menuForm.invalid) return;

    if (!this.menuItems.length) {
      this.snackBar.openSnackBar({ data: { message: this.translate('menu_management.validation.add_one_menu_item') } });
      return;
    }

    const hasInvalidMenuItems = this.menuItems.some(item => !this.validateMenuItem(item).valid);
    if (hasInvalidMenuItems) {
      this.snackBar.openSnackBar({ data: { message: this.translate('menu_management.validation.complete_menu_items') } });
      return;
    }

    this.isSubmitting = true;

    const formValue = this.menuForm.getRawValue();
    const payload = {
      ...formValue,
      menu_name: formValue.menu_name.trim(),
      menu_items: this.menuItems,
      hasSearchField: this.hasSearchField
    };

    if (this.menuToEdit) {
      this.menuService.updateMenu(this.menuToEdit.id, payload).subscribe({
        next: () => {
          this.snackBar.openSnackBar({
            data: { message: this.translate('menu_management.messages.update_success') },
            duration: 3000,
            panelClass: SnackBarUtil.CLASSES.success,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.router.navigate(['/menu']);
        },
        error: () => (this.isSubmitting = false)
      });
    } else {
      this.menuService.createMenu(payload).subscribe({
        next: () => {
          this.snackBar.openSnackBar({
            data: { message: this.translate('menu_management.messages.create_success') },
            duration: 3000,
            panelClass: SnackBarUtil.CLASSES.success,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.router.navigate(['/menu']);
        },
        error: (error: HttpErrorResponse) => {
          this.isSubmitting = false;
          if (error.status === 409) {
            this.errorMessages['menu_name'] = this.translate('menu_management.validation.menu_name_exists');
          }
        }
      });
    }
  }

  get mobilePageTitleKey(): string {
    if (this.mobileStep === 'basic') {
      return 'menu_management.basic_information';
    }
    return this.menuToEdit ? 'menu_management.edit_menu' : 'menu_management.create_the_menu';
  }

  goToMobileItemsStep(): void {
    this.menuForm.markAllAsTouched();
    if (!this.validateForm() || this.menuForm.invalid) {
      return;
    }
    this.mobileStep = 'items';
  }

  onMobileBack(): void {
    if (this.mobileStep === 'items') {
      this.mobileStep = 'basic';
      return;
    }
    this.cancel();
  }

  cancel() {
    if (this.hasUnsavedChanges) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.width = '400px';
      dialogConfig.data = {
        title: this.translate('menu_management.unsaved_changes.title'),
        message: this.translate('menu_management.unsaved_changes.message'),
        confirmText: this.translate('menu_management.unsaved_changes.leave'),
        cancelText: this.translate('menu_management.unsaved_changes.stay')
      };
      this.router.navigate(['/menu']);
    } else {
      this.router.navigate(['/menu']);
    }
  }

  setAllowsMultipleProducts(checked: boolean): void {
    this.menuForm.get('allowsMultipleProducts')?.setValue(checked);
  }

  onAddSearch() {
    if (!this.hasSearchField) {
      this.hasSearchField = true;
    }
  }

  removeSearchField() {
    this.hasSearchField = false;
    this.searchQuery = '';
    this.applySearchFilter();
  }

  onSearchQueryChange() {
    this.applySearchFilter();
  }

  clearSearch() {
    this.searchQuery = '';
    this.applySearchFilter();
  }

  applySearchFilter() {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredMenuItems = [...this.menuItems];
    } else {
      const query = this.searchQuery.toLowerCase().trim();
      this.filteredMenuItems = this.menuItems.filter(item =>
        item.name.toLowerCase().includes(query)
      );
    }
    this.currentItemsPage = 0;
  }

  getPaginatedItems() {
    const start = this.currentItemsPage * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredMenuItems.slice(start, end);
  }

  getTotalPages(): number {
    return Math.ceil(this.filteredMenuItems.length / this.itemsPerPage);
  }

  goToItemsPage(page: number) {
    if (page >= 0 && page < this.getTotalPages()) {
      this.currentItemsPage = page;
    }
  }

  getPageNumbers(): number[] {
    return this.getPageUtil.getPageNumbers(this.currentItemsPage, this.filteredMenuItems.length, this.itemsPerPage);
  }

  onAddMenuItemWithQuantity() {
    this.addNewMenuItem(true);
  }

  increaseQuantity(itemId: string) {
    const item = this.menuItems.find(i => i.id === itemId);
    if (item && item.hasQuantity) {
      const currentValue = item.quantity ? parseFloat(item.quantity) || 0 : 0;
      item.quantity = String(currentValue + 1);
    }
  }

  decreaseQuantity(itemId: string) {
    const item = this.menuItems.find(i => i.id === itemId);
    if (item && item.hasQuantity && item.quantity) {
      const currentValue = parseFloat(item.quantity) || 0;
      if (currentValue > 1) {
        item.quantity = String(currentValue - 1);
      }
    }
  }

  onAddStandardMenuItem() {
    this.addNewMenuItem(false);
  }

  private addNewMenuItem(hasQuantity: boolean) {
    const newItem = this.createNewMenuItem(hasQuantity);
    this.menuItems.unshift(newItem);
    this.menuItems.forEach((item, index) => {
      item.displayOrder = index;
    });
    this.currentItemsPage = 0;
    this.hasUnsavedChanges = true;
    this.applySearchFilter();
    this.updateMultiSelectAvailability();
    this.changeDetectorRef.detectChanges();
  }

  private createNewMenuItem(hasQuantity: boolean): typeof this.menuItems[0] {
    const newItem = {
      id: `item-${Date.now()}`,
      name: '',
      description: '',
      price: hasQuantity ? 0 : undefined,
      image: null,
      selected: false,
      hasQuantity,
      quantity: hasQuantity ? '' : undefined,
      displayOrder: 0
    };
    return newItem;
  }

  selectMenuItem(itemId: string) {
    if (this.selectedMenuItemId === itemId) {
      this.selectedMenuItemId = null;
    } else {
      this.selectedMenuItemId = itemId;
    }
    this.changeDetectorRef.detectChanges();
  }

  isMenuItemSelected(itemId: string): boolean {
    return this.selectedMenuItemId === itemId;
  }

  drop(event: CdkDragDrop<any[]>) {
    const startIndex = this.currentItemsPage * this.itemsPerPage;
    const previousIndexInFiltered = startIndex + event.previousIndex;
    const currentIndexInFiltered = startIndex + event.currentIndex;

    if (previousIndexInFiltered >= 0 && currentIndexInFiltered >= 0 &&
        previousIndexInFiltered < this.filteredMenuItems.length &&
        currentIndexInFiltered < this.filteredMenuItems.length) {
      moveItemInArray(this.filteredMenuItems, previousIndexInFiltered, currentIndexInFiltered);

      const reorderedItems = this.filteredMenuItems.map(filteredItem =>
        this.menuItems.find(item => item.id === filteredItem.id)!
      ).filter(item => item !== undefined);

      const filteredIds = new Set(this.filteredMenuItems.map(item => item.id));
      const otherItems = this.menuItems.filter(item => !filteredIds.has(item.id));

      this.menuItems = [...reorderedItems, ...otherItems];

      this.menuItems.forEach((item, index) => {
        item.displayOrder = index;
      });

      this.hasUnsavedChanges = true;
    }
  }

  removeMenuItem(itemId: string) {
    this.menuItems = this.menuItems.filter(item => item.id !== itemId);
    this.menuItems.forEach((item, index) => {
      item.displayOrder = index;
    });
    this.hasUnsavedChanges = true;
    this.applySearchFilter();
    this.updateMultiSelectAvailability();
  }

  removeMenuItemImage(itemId: string) {
    const item = this.menuItems.find(i => i.id === itemId);
    if (item) {
      item.image = null;
      item.imageError = null;
    }
  }

  onImageSelect(event: Event, itemId: string) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'heic'];
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      const item = this.menuItems.find(i => i.id === itemId);
      if (!allowedExtensions.includes(ext)) {
        if (item) {
          item.imageError = this.translate('menu_management.validation.invalid_image_format');
          item.image = null;
          this.changeDetectorRef.detectChanges();
        }
        input.value = '';
        return;
      }
      if (item) {
        item.imageError = null;
      }
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (item && e.target?.result) {
          item.image = e.target.result as string;
          if (!item.imageName) {
            item.imageName = file.name.substring(0, 20);
          }
          this.hasUnsavedChanges = true;
          this.changeDetectorRef.detectChanges();
        }
      };
      reader.readAsDataURL(file);
    }
  }

  validateMenuItem(item: typeof this.menuItems[0]): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    if (!item.name || item.name.trim() === '') {
      errors.push(this.translate('menu_management.validation.enter_product_name'));
    } else if (item.name.length > 20) {
      errors.push(this.translate('menu_management.validation.max_20'));
    } else if (!/[a-zA-Z0-9]/.test(item.name)) {
      errors.push(this.translate('menu_management.validation.letter_or_number'));
    }

    if (item.hasQuantity) {
      if (!item.quantity || item.quantity.trim() === '') {
        errors.push(this.translate('menu_management.validation.enter_quantity'));
      } else if (isNaN(parseFloat(item.quantity)) || parseFloat(item.quantity) <= 0) {
        errors.push(this.translate('menu_management.validation.positive_quantity'));
      }
    } else {
      if (!item.price || item.price <= 0) {
        errors.push(this.translate('menu_management.validation.enter_price'));
      } else if (isNaN(item.price)) {
        errors.push(this.translate('menu_management.validation.only_numbers'));
      }
    }

    if (item.description && item.description.length > 30) {
      errors.push(this.translate('menu_management.validation.description_max_30'));
    } else if (item.description && !/[a-zA-Z0-9]/.test(item.description)) {
      errors.push(this.translate('menu_management.validation.letter_or_number'));
    }
    if (item.image && item.imageName && item.imageName.length > 20) {
      errors.push(this.translate('menu_management.validation.image_name_max_20'));
    }
    if (item.imageError) {
      errors.push(item.imageError);
    }
    return { valid: errors.length === 0, errors };
  }

  get canSave(): boolean {
    if (this.menuItems.length === 0) {
      return false;
    }
    return this.menuItems.every(item => this.validateMenuItem(item).valid);
  }


  onPriceInput(event: Event, item: typeof this.menuItems[0]) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    if (value === '' || value === null || value === undefined) {
      item.price = undefined;
    } else {
      const numValue = Number(value);
      item.price = isNaN(numValue) ? undefined : numValue;
    }
  }

  onShowQr() {
    if (!this.menuToEdit || !this.menuToEdit.id) {
      this.snackBar.openSnackBar({
        data: { message: this.translate('menu_management.validation.save_before_qr') },
        duration: 3000,
        panelClass: SnackBarUtil.CLASSES.error,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      return;
    }

    this.menuService.generateMenuQrCode(this.menuToEdit.id).subscribe({
      next: (qrCodeBase64: string) => {
        const menuUrl = `${environment.apiUrl.replace('/api', '')}/menu/view/${this.menuToEdit!.id}`;
        const dialogConfig = new MatDialogConfig();
        dialogConfig.width = '500px';
        dialogConfig.maxWidth = '90vw';
        dialogConfig.disableClose = false;
        dialogConfig.data = {
          menuName: this.menuToEdit!.menu_name,
          address: this.menuToEdit!.address || '',
          qrCodeBase64: qrCodeBase64,
          menuUrl: menuUrl
        };

        this.dialog.open(MenuShowQrModalComponent, dialogConfig);
      },
      error: (error: HttpErrorResponse) => {
        this.snackBar.openSnackBar({
          data: { message: this.translate('menu_management.errors.qr_generate') },
          duration: 3000,
          panelClass: SnackBarUtil.CLASSES.error,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      }
    });
  }
}
